import { useEffect, useLayoutEffect } from 'react';

import { useSuperState } from 'hooks/useSuperState';

/**
 * Bootstrap 4 breakpoints, ordered ascending by their min-width (px). Each entry
 * marks the lower edge of its band; the band extends up to the next entry.
 */
const BOOTSTRAP_BREAKPOINTS = [
    { name: 'xs', value: 0 },
    { name: 'sm', value: 576 },
    { name: 'md', value: 768 },
    { name: 'lg', value: 992 },
    { name: 'xl', value: 1200 }
] as const;

export type BootstrapBreakpoint = (typeof BOOTSTRAP_BREAKPOINTS)[number]['name'];

/** A breakpoint expressed either as a Bootstrap name or a raw pixel value. */
export type Breakpoint = BootstrapBreakpoint | number;

/**
 * Sub-pixel nudge applied to exclusive boundaries. media `max-width` is inclusive,
 * so we subtract this to express "strictly less than" without overlapping the next
 * breakpoint (the same technique Bootstrap uses for its own breakpoint maxes).
 */
const STEP = 0.02;

type WindowDimensionsOptions = {

    /** Track whether the viewport width falls within [min, max). */
    range?: [min: Breakpoint, max: Breakpoint];

    /** Compare the viewport width against this breakpoint for above/below/equal. */
    target?: Breakpoint;
};

type WindowDimensions = {
    height: number;

    /** Width sits above the target breakpoint (or its band, for named targets). */
    isAbove: boolean;

    /** Width sits below the target breakpoint. */
    isBelow: boolean;

    /** Width matches the target breakpoint's band (named) or exact value (numeric). */
    isEqual: boolean;

    /** Width falls within the configured range. */
    isInRange: boolean;
    width: number;
};

type DimensionState = {
    above: boolean;
    below: boolean;
    height: number;
    inRange: boolean;
    width: number;
};

const resolveBreakpoint = (breakpoint: Breakpoint): number => typeof breakpoint === 'number'
    ? breakpoint
    : BOOTSTRAP_BREAKPOINTS.find(bp => bp.name === breakpoint)?.value ?? 0;

/** Returns the [lower, upper) pixel band a named breakpoint occupies. */
const getBreakpointBand = (breakpoint: BootstrapBreakpoint): [number, number] => {

    const index = BOOTSTRAP_BREAKPOINTS.findIndex(bp => bp.name === breakpoint);

    return [BOOTSTRAP_BREAKPOINTS[index].value, BOOTSTRAP_BREAKPOINTS[index + 1]?.value ?? Infinity];

};

/** Builds a media query string matching widths inside the resolved [min, max) range. */
const buildRangeQuery = (range: [Breakpoint, Breakpoint]): string => {

    const edges = [resolveBreakpoint(range[0]), resolveBreakpoint(range[1])];
    const lower = Math.min(...edges);
    const upper = Math.max(...edges);

    const conditions: string[] = [];

    if (lower > 0) {

        conditions.push(`(min-width: ${lower}px)`);

    }

    if (Number.isFinite(upper)) {

        conditions.push(`(max-width: ${upper - STEP}px)`);

    }

    return conditions.length > 0 ? conditions.join(' and ') : '(min-width: 0px)';

};

/**
 * Builds the "below" and "above" media queries for a target. "Equal" is derived as
 * neither matching, which keeps numeric equality robust against sub-pixel/zoom width.
 *
 * Numeric target v: below = width < v, above = width > v.
 * Named target band [lo, hi): below = width < lo, above = width >= hi.
 */
const buildTargetQueries = (target: Breakpoint): { above: string | null; below: string | null } => {

    const [lower, upper] = typeof target === 'number' ? [target, target + STEP] : getBreakpointBand(target);
    const belowMax = lower - STEP;

    return {
        above: Number.isFinite(upper) ? `(min-width: ${upper}px)` : null,
        below: belowMax >= 0 ? `(max-width: ${belowMax}px)` : null
    };

};

/**
 * Tracks viewport dimensions and derives breakpoint-aware flags.
 *
 * Breakpoints may be supplied as Bootstrap names ('xs'|'sm'|'md'|'lg'|'xl') or raw
 * pixel numbers in both `range` and `target`.
 *
 * The breakpoint flags (isInRange/isAbove/isBelow/isEqual) are driven by `matchMedia`,
 * so they update only when a boundary is actually crossed — no debounce, no latency.
 * The live `width`/`height` are tracked via a requestAnimationFrame-throttled resize
 * listener, coalescing bursts of resize events into a single layout read per frame.
 *
 * @param options - { range: [min, max], target }
 * @returns { width, height, isInRange, isAbove, isBelow, isEqual }
 */
export const useWindowDimensions = (options: WindowDimensionsOptions = {}): WindowDimensions => {

    const { range, target } = options;

    const [state, setState] = useSuperState<DimensionState>({
        above: false,
        below: false,
        height: window.innerHeight,
        inRange: false,
        width: window.innerWidth
    });

    const rangeQuery = range ? buildRangeQuery(range) : null;
    const targetQueries = target !== undefined ? buildTargetQueries(target) : null;
    const aboveQuery = targetQueries?.above ?? null;
    const belowQuery = targetQueries?.below ?? null;

    // Drive the breakpoint flags from matchMedia. useLayoutEffect (not useEffect) so the
    // first correct value is committed before paint, avoiding a flash of stale flags.
    useLayoutEffect(() => {

        const cleanups: Array<() => void> = [];
        const initial: Partial<DimensionState> = { above: false, below: false, inRange: false };

        const subscribe = (query: string | null, assign: (target: Partial<DimensionState>, matches: boolean) => void) => {

            if (query === null) {

                assign(initial, false);
                return;

            }

            const mql = window.matchMedia(query);

            assign(initial, mql.matches);

            const handler = () => {

                const update: Partial<DimensionState> = {};
                assign(update, mql.matches);
                setState(update);

            };

            mql.addEventListener('change', handler);
            cleanups.push(() => mql.removeEventListener('change', handler));

        };

        subscribe(rangeQuery, (slice, matches) => {

            slice.inRange = matches;

        });
        subscribe(belowQuery, (slice, matches) => {

            slice.below = matches;

        });
        subscribe(aboveQuery, (slice, matches) => {

            slice.above = matches;

        });

        setState(initial);

        return () => cleanups.forEach(cleanup => cleanup());

    }, [rangeQuery, belowQuery, aboveQuery]); // eslint-disable-line react-hooks/exhaustive-deps

    // Track live dimensions with a requestAnimationFrame throttle: at most one reflow
    // (innerWidth/innerHeight read) per frame, and the final settled size is captured.
    useEffect(() => {

        let frame = 0;

        const handleResize = () => {

            if (frame !== 0) {

                return;

            }

            frame = requestAnimationFrame(() => {

                frame = 0;
                setState({ height: window.innerHeight, width: window.innerWidth });

            });

        };

        window.addEventListener('resize', handleResize);

        return () => {

            window.removeEventListener('resize', handleResize);

            if (frame !== 0) {

                cancelAnimationFrame(frame);

            }

        };

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const hasTarget = target !== undefined;

    return {
        height: state.height,
        isAbove: hasTarget && state.above,
        isBelow: hasTarget && state.below,
        isEqual: hasTarget && !state.above && !state.below,
        isInRange: state.inRange,
        width: state.width
    };

};